import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { createUseStyles } from 'react-jss'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { IAppState } from 'infra/AppState'
import { ICustomer, ISupplier } from 'state/dashboard/dashboard.types'
import {
  broadcastEvent,
  hideTxnAlertNudge,
  markTxnAlertSettingSeen
} from 'state/dashboard/dashboard.actions'
import { THEME } from 'components/theme'
import { ENTITY_TYPE } from 'shared/constants'
import { TransactionAlertSettings } from './TransactionAlertSettings'

const useStyles = createUseStyles({
  scrim: {
    position: 'fixed',
    inset: 0,
    zIndex: 100001,
    backgroundColor: 'rgba(0, 0, 0, 0.32)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    boxSizing: 'border-box'
  },
  dialog: {
    backgroundColor: THEME.brandWhite,
    borderRadius: 16,
    width: 480,
    maxWidth: 'calc(100vw - 48px)',
    maxHeight: 'calc(100vh - 48px)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  // iOS top bar: name (bold) over mobile, a back/close control on the left
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px 16px',
    borderBottom: `1px solid ${THEME.brandLightGrey}`
  },
  close: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 22,
    lineHeight: 1,
    color: THEME.brandBlack,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: THEME.brandGrey
    }
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0
  },
  name: {
    fontSize: 16,
    fontWeight: 600,
    color: THEME.brandBlack,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  mobile: {
    fontSize: 13,
    color: THEME.brandDarkerGrey
  },
  body: {
    overflowY: 'auto',
    flex: 1
  },
  footer: {
    padding: '12px 16px 16px',
    backgroundColor: THEME.brandWhite
  },
  done: {
    width: '100%',
    height: 48,
    borderRadius: 24,
    border: 'none',
    background: THEME.brandGreen,
    color: THEME.onBrand,
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': {
      filter: 'brightness(0.95)'
    }
  }
})

interface TransactionAlertDialogProps {
  entityId: string
  entityType: ENTITY_TYPE
  onClose: () => void
}

// Standalone Transaction Alert settings (iOS TransactionalAlertSettingsScreen as a dialog):
// header with the account's name + mobile, the two settings cards, a pinned Done.
export const TransactionAlertDialog: React.FC<TransactionAlertDialogProps> = ({
  entityId,
  entityType,
  onClose
}) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const isCustomer = entityType === ENTITY_TYPE.CUSTOMER
  const entity = useSelector(
    (state: IAppState) =>
      state.Dashboard[isCustomer ? 'customers' : 'suppliers'].byIds[entityId] as
        | ICustomer
        | ISupplier
        | undefined
  )
  if (!entity) {
    return null
  }
  const name = isCustomer ? (entity as ICustomer).description : (entity as ISupplier).name

  return createPortal(
    <div className={classes.scrim} onClick={onClose}>
      <div
        className={classes.dialog}
        role="dialog"
        aria-modal="true"
        aria-label={t('dashboard:TxnAlertTitle')}
        data-sheet-anchor=""
        onClick={(e) => e.stopPropagation()}
      >
        <div className={classes.header}>
          <button
            type="button"
            className={classes.close}
            onClick={onClose}
            aria-label={t('dashboard:Cancel')}
          >
            ×
          </button>
          <div className={classes.headerText}>
            <span className={classes.name}>{name}</span>
            <span className={classes.mobile}>{entity.mobile}</span>
          </div>
        </div>
        <div className={classes.body}>
          <TransactionAlertSettings
            entityId={entityId}
            entityType={entityType}
            source="add_transaction_nudge"
          />
        </div>
        <div className={classes.footer}>
          <button type="button" className={classes.done} onClick={onClose}>
            {t('dashboard:Done')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// Mounted once in the dashboard shell. Shows the dialog when the add-transaction saga asks
// for the nudge (Dashboard.txnAlertNudge); Done/close marks the setting as seen on the
// backend (display_txn_alert_setting) so the account is nudged only once — iOS/Android parity.
export const TransactionAlertNudge: React.FC = () => {
  const dispatch = useDispatch()
  const nudge = useSelector((state: IAppState) => state.Dashboard.txnAlertNudge)
  // Web-only: the post-transaction nudge appeared for this account
  useEffect(() => {
    if (nudge) {
      dispatch(
        broadcastEvent('txn_alert_nudge_viewed', {
          relation: nudge.entityType,
          account_id: nudge.entityId
        })
      )
    }
  }, [nudge, dispatch])
  if (!nudge) {
    return null
  }
  const onClose = () => {
    dispatch(
      broadcastEvent('txn_alert_nudge_done', {
        relation: nudge.entityType,
        account_id: nudge.entityId
      })
    )
    dispatch(markTxnAlertSettingSeen(nudge))
    dispatch(hideTxnAlertNudge())
  }
  return (
    <TransactionAlertDialog
      entityId={nudge.entityId}
      entityType={nudge.entityType}
      onClose={onClose}
    />
  )
}

export default TransactionAlertDialog
